import { Close } from '@mui/icons-material';
import {
  Button, Dialog, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { useEffect, useState } from 'react';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { searchBuildingUnits, updateUnit } from 'shared/api/units';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { useRequest } from 'shared/hooks';
import VlanEditDataGrid from 'features/networking/building/VlanEditDataGrid';

/**
 * @param {Object} props
 * @param {number} props.buildingId - Building ID
 * @param {number} [props.minVlan=0] - Minimum VLAN value (default 0 to lock ability to
 *   change unit vlan when min/max is null in db)
 * @param {number} [props.maxVlan=0] - Maximum VLAN value (default 0 to lock ability to
 *   change unit vlan when min/max is null in db)
 * @param {number} props.updateCount - Update counter
 */
export default function VlanEditDialog({
  buildingId,
  minVlan = 0,
  maxVlan = 0,
  updateCount,
}) {
  const { request } = useRequest();
  const [isOpen, setIsOpen] = useState(false);
  const [units, setUnits] = useState(undefined);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    (async () => {
      const response = await request(searchBuildingUnits('', '', buildingId));
      if (response) {
        setUnits(response?.rows);
      }
    })();
  }, [buildingId, request, updateCount]);

  /**
   * @param {Array} rows - VLAN changeset from child component
   */
  const handleSave = async (rows) => {
    // get all child rows that differ from parent unit rows
    const changedRows = rows.filter((row) => {
      const match = units.find((u) => u.id === row.id);
      return match.vlan !== row.vlan;
    });
    const reqs = [...changedRows.map((cr) => updateUnit(cr.id, {
      building_id: buildingId,
      vlan: cr.vlan,
    }))];

    const rowResults = await Promise.all(reqs.map((r) => request(r, 'Updated Vlans')));
    setUnits((oldUnits) => oldUnits.map((oldUnit) => {
      const newUnit = rowResults.find((row) => row.id === oldUnit.id);
      return newUnit === undefined ? oldUnit : newUnit;
    }));
  };

  return (
    <>
      <Button
        variant={VARIANTS.contained}
        color={BUTTON_COLORS.primary}
        startIcon={<MeetingRoomIcon />}
        onClick={() => setIsOpen(true)}
        disabled={!units}
      >
        Manage Units
      </Button>
      {units && (
      <Dialog
        open={isOpen}
        keepMounted
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <DialogTitle>
          Configure Vlans
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <VlanEditDataGrid
            buildingID={buildingId}
            data={units}
            minVlan={minVlan}
            maxVlan={maxVlan}
            onSave={handleSave}
          />
        </DialogContent>
      </Dialog>
      )}
    </>
  );
}
